import { ReloadOutlined } from '@ant-design/icons';
import { Button, Collapse } from 'antd';
import Badge from 'components/Badge';
import LoadingSpinner from 'components/LoadingSpinner';
import {
  resetFilters,
  setFilteredLots,
  setSelectedFilters,
  setShowFilterPopup,
  setTotalSelectedFilters,
} from 'features/siteMapSlice';
import gaFunction from 'lib/ga';
import { useSiteMapQuery } from 'lib/hooks/queries';
import { useAppDispatch, useAppSelector } from 'lib/hooks/redux';
import useClickAway from 'lib/hooks/useClickAway';
import useWindowDimensions from 'lib/hooks/useWindowDimensions';
import { resetFilterSVG, showAndGetFilteredLots } from 'lib/utils';
import { useMemo, useRef } from 'react';
import styled from 'styled-components';

const { Panel } = Collapse;

interface Props {
  navPositionX: number;
}

const FilterMenuPopup = ({ navPositionX }: Props) => {
  // Redux Hooks and States
  const dispatch = useAppDispatch();
  const { filterDictionary, selectedFilters, totalSelectedFilters } = useAppSelector((state) => ({
    filterDictionary: state.siteMap.filterDictionary,
    selectedFilters: state.siteMap.selectedFilters,
    totalSelectedFilters: state.siteMap.totalSelectedFilters,
  }));

  // Get Site Map Data from React-Query Cache (No Params)
  const siteMapData = useSiteMapQuery().data!;

  // Constants
  const URLParams = new URLSearchParams(window.location.search);
  const filterPlanSysId = URLParams.get('filterPlanSysId');

  // React/Custom Hooks
  const filterMenuRef = useRef(null);
  useClickAway({
    ref: filterMenuRef,
    onClickAway: () => dispatch(setShowFilterPopup(false)),
  });
  // variables for responsive filter-menu styling
  const leftSideOfScreen = useWindowDimensions().width / 2;
  const isNavOnLeft = useMemo(
    () => navPositionX! < leftSideOfScreen,
    [navPositionX, leftSideOfScreen]
  );

  // Conditions
  if (!filterDictionary) {
    return <LoadingSpinner />;
  }

  // Functions
  const handleClick = ({
    filterName,
    filterSysId,
  }: {
    filterName: string;
    filterSysId: string;
  }) => {
    let newSelectedFilters = { ...selectedFilters };
    let newTotalSelectedFilters = totalSelectedFilters;
    if (newSelectedFilters[filterName].includes(filterSysId)) {
      newSelectedFilters[filterName] = newSelectedFilters[filterName].filter(
        (sysId) => sysId !== filterSysId
      );
      newTotalSelectedFilters--;
    } else {
      const newFilters = [...newSelectedFilters[filterName]];
      newFilters.push(filterSysId);
      newSelectedFilters[filterName] = [...newFilters];
      newTotalSelectedFilters++;
      // GA
      const { title, filters } = filterDictionary[filterName];
      const selectedFilter = filters.filter(({ sysId }) => sysId === filterSysId);
      gaFunction.spFilterSelected(title, selectedFilter[0].name);
    }

    dispatch(setTotalSelectedFilters(newTotalSelectedFilters));
    dispatch(setSelectedFilters(newSelectedFilters));

    if (newTotalSelectedFilters === 0) {
      // if there is no selected filters
      resetFilterSVG(siteMapData);
      dispatch(setFilteredLots([]));
    } else {
      // get associated lots and combine them
      const filteredLots = showAndGetFilteredLots({
        filterDictionary,
        selectedFilters: newSelectedFilters,
        siteMapData,
      });
      dispatch(setFilteredLots(filteredLots));
    }
  };

  const handleReset = () => {
    if (totalSelectedFilters > 0) {
      resetFilterSVG(siteMapData);
      dispatch(resetFilters());
    }
  };

  return (
    <StyledFilterMenuPopup
      ref={filterMenuRef}
      isNavOnLeft={isNavOnLeft}
      data-cy="filter-menu-popup"
    >
      <Collapse accordion>
        {Object.entries(filterDictionary).map(([filterName, filterMenu]) => (
          <Panel
            header={filterMenu.title}
            key={filterMenu.title}
            extra={
              selectedFilters[filterName].length > 0 && (
                <Badge text={selectedFilters[filterName].length} />
              )
            }
          >
            {filterMenu.filters.map((filter) => (
              <Button
                key={filter.sysId}
                className={`filter-button ${
                  selectedFilters[filterName].includes(filter.sysId) ? 'selected' : ''
                }`}
                disabled={!!filterPlanSysId && filterName === 'planName'}
                onClick={() =>
                  handleClick({
                    filterName,
                    filterSysId: filter.sysId,
                  })
                }
              >
                {filter.name.toLocaleString()}
              </Button>
            ))}
          </Panel>
        ))}
      </Collapse>
      {!filterPlanSysId && (
        <Button className="reset-button" data-cy="filter-reset-button" onClick={handleReset}>
          <ReloadOutlined />
          Reset Filter
        </Button>
      )}
    </StyledFilterMenuPopup>
  );
};

export default FilterMenuPopup;

const StyledFilterMenuPopup = styled.div<{ isNavOnLeft: boolean }>`
  position: absolute;
  max-height: 95vh;
  overflow-y: auto;
  top: 13px;
  ${({ isNavOnLeft }) =>
    isNavOnLeft ? `left: min(10vw + 5px, 100px);` : `right: min(10vw + 5px, 100px);`}
  z-index: 200;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.bgColor};
  box-shadow: rgba(99, 99, 99, 0.2) 0px 2px 8px 0px;
  .ant-collapse {
    min-width: 300px;
    .ant-collapse-header {
      color: ${({ theme }) => theme.colors.primaryColor};
      &:hover {
        background-color: #dfdfdf;
      }
    }
    .ant-collapse-content-box {
      display: flex;
      flex-direction: column;
      .filter-button {
        margin: 5px 0;
        height: 40px;
        border-radius: 5px;
        background: ${({ theme }) => theme.colors.textColor};
        color: ${({ theme }) => theme.colors.primaryColor};
        &:hover:enabled {
          background: ${({ theme }) => theme.colors.primaryColor};
          color: ${({ theme }) => theme.colors.textColor};
          border-color: ${({ theme }) => theme.colors.primaryColor};
        }
      }
      .selected {
        background: ${({ theme }) => theme.colors.primaryColor};
        color: ${({ theme }) => theme.colors.textColor};
      }
    }
  }
  .reset-button {
    margin: 0;
    height: 40px;
    background: ${({ theme }) => theme.colors.bgColor};
    color: ${({ theme }) => theme.colors.primaryColor};
    &:hover {
      border-color: ${({ theme }) => theme.colors.primaryColor};
      background-color: #dfdfdf;
    }
    &:active {
      background-color: lightgray;
    }
  }
  @media (max-width: 567px), (max-height: 567px) {
    top: 58px;
    right: unset;
    left: 5px;
    max-height: 95vh;
    .ant-collapse {
      min-width: 200px;
      font-size: 13px;
    }
    .ant-btn {
      font-size: 13px;
    }
  }
`;
